// One product must be planned in a single concentration unit category
// throughout a trial: a recorded mix stores one quantity per product, in the
// absolute counterpart of that category (kg for kg/ha, l for l/ha), so a
// product planned by weight in one cell and by volume in another would have
// no single quantity unit.

/** One application's use of a product, located by the table cell it is in. */
export interface ProductUnitCategoryUsage {
  productId: string;
  productName: string;
  unitCategory: string;
  scheduledVisitName: string;
  treatmentName: string;
}

export interface ProductUnitCategoryConflict {
  productId: string;
  productName: string;
  /** the categories in use, each with the "scheduled visit / treatment" cells using it */
  cells: { unitCategory: string; cellNames: string[] }[];
}

export function findProductUnitCategoryConflicts(
  usages: ProductUnitCategoryUsage[]
): ProductUnitCategoryConflict[] {
  const conflicts: ProductUnitCategoryConflict[] = [];
  const byProductId = new Map<string, ProductUnitCategoryUsage[]>();
  for (const usage of usages) {
    const productUsages = byProductId.get(usage.productId) ?? [];
    productUsages.push(usage);
    byProductId.set(usage.productId, productUsages);
  }

  byProductId.forEach((productUsages, productId) => {
    const cellNamesByCategory = new Map<string, string[]>();
    for (const usage of productUsages) {
      const cellNames = cellNamesByCategory.get(usage.unitCategory) ?? [];
      cellNames.push(`${usage.scheduledVisitName} / ${usage.treatmentName}`);
      cellNamesByCategory.set(usage.unitCategory, cellNames);
    }
    if (cellNamesByCategory.size < 2) {
      return;
    }

    const cells: ProductUnitCategoryConflict['cells'] = [];
    cellNamesByCategory.forEach((cellNames, unitCategory) => {
      cells.push({ unitCategory, cellNames });
    });
    conflicts.push({ productId, productName: productUsages[0].productName, cells });
  });

  return conflicts;
}

/** "Nitrogen: Weight per area (Week 1 / T1), Volume per area (Week 2 / T1, Week 2 / T2)" */
export function describeProductUnitCategoryConflict(
  conflict: ProductUnitCategoryConflict,
  unitCategoryNames: Map<string, string>
): string {
  const categories = conflict.cells.map(
    ({ unitCategory, cellNames }) =>
      `${unitCategoryNames.get(unitCategory) ?? unitCategory} (${cellNames.join(', ')})`
  );

  return `${conflict.productName}: ${categories.join(', ')}`;
}

export const CONCENTRATION_UNIT_CATEGORIES = ['weight_per_area', 'volume_per_area'];

/**
 * Per product, the one unit category its applications use. A product whose
 * applications already disagree is left out: nothing can be inferred for it.
 */
export function plannedUnitCategoryByProductId(
  usages: Pick<ProductUnitCategoryUsage, 'productId' | 'unitCategory'>[]
): Map<string, string> {
  const categories = new Map<string, string>();
  const inconsistentProductIds = new Set<string>();
  for (const { productId, unitCategory } of usages) {
    const planned = categories.get(productId);
    if (planned !== undefined && planned !== unitCategory) {
      inconsistentProductIds.add(productId);
    }
    categories.set(productId, unitCategory);
  }
  inconsistentProductIds.forEach((productId) => categories.delete(productId));

  return categories;
}

/**
 * The categories a tab's unit picker may offer. Outside drafts a saved
 * application keeps its category: the mixes recorded against it hold
 * quantities in that category's absolute counterpart. Otherwise the product's
 * category elsewhere in the trial, when there is one, is the only valid pick.
 */
export function selectableConcentrationUnitCategories(
  isDraft: boolean,
  initialUnitCategory: string,
  plannedUnitCategory = ''
): string[] {
  if (!isDraft && initialUnitCategory) {
    return [initialUnitCategory];
  }
  if (plannedUnitCategory) {
    return [plannedUnitCategory];
  }

  return CONCENTRATION_UNIT_CATEGORIES;
}
